import React, { useState } from 'react'
import { css } from '@emotion/core'
import type { Person } from '@fs/zion-tree-types'
import { MediaCamera, NoticeLoading } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { WhiteTheme, Avatar, useOverlay } from '@fs/zion-ui'
import usePortraitStatus from '../../../data-service/usePortraitStatus'
import PortraitOverlay from '../banners/PortraitOverlay'

const portraitSelectorCss = css`
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  height: 24px;
  width: 24px;
  border-radius: 24px;
  background: none;
  cursor: pointer;
  transition: all 0.15s;
  border: none;
  outline: none;
  backdrop-filter: brightness(0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  font-size: 12px;
  text-align: center;
  text-shadow: 0 0 8px rgb(0, 0, 0, 0.5);
  &[data-status],
  &[data-refreshing],
  &:hover,
  &:focus {
    opacity: 1;
  }
  &[aria-disabled='true'] {
    cursor: default;
  }

  &[avatar-size='sm'] {
    height: 40px;
    width: 40px;
    border-radius: 40px;
  }
  &[avatar-size='md'] {
    height: 64px;
    width: 64px;
    border-radius: 64px;
  }
  &[avatar-size='lg'] {
    height: 88px;
    width: 88px;
    border-radius: 88px;
  }
`
const fontSizeCss = css`
  font-size: inherit;
  &[avatar-size='xs'] {
    font-size: 4px;
  }
  &[avatar-size='sm'] {
    font-size: 6px;
  }
  &[avatar-size='md'] {
    font-size: 10px;
  }
`

type Props = {
  focusPerson?: Person
  size: 'xs' | 'sm' | 'md' | 'lg'
}

/**
 * Person does not provide a way for a callback to ran when the avatar is clicked.
 * but gold is going to add a way to hide the avatar. At that point, we'll refactor
 * the person banner to use Avatar directly so we can wrap it with this instead
 * of absolute positioning this component.
 *
 */
export default function PortraitSelectorButton({ focusPerson, size }: Props): JSX.Element | null {
  const [t] = useTranslation()
  const portraitOverlayProps = useOverlay()
  // True from a saved portrait change until the focus person revalidates and the avatar can update
  const [avatarRefreshing, setAvatarRefreshing] = useState(false)
  const portraitStatus = usePortraitStatus(focusPerson?.id)
  const nonReadyStatusKey =
    portraitStatus === 'failed' ? 'tree-person.upload-failed.description' : 'person-page-banner.processing.badge'
  const status = portraitStatus && portraitStatus !== 'ready' ? t(nonReadyStatusKey) : undefined

  if (!focusPerson) return null

  return (
    <div style={{ position: 'relative' }}>
      <Avatar
        src={focusPerson.portraitUrl}
        sex={focusPerson.gender?.toLowerCase() ?? 'unknown'}
        size={size}
        gray={focusPerson.deleted}
      />
      {!focusPerson.readOnly && !focusPerson.deleted && (
        <>
          <button
            css={portraitSelectorCss}
            avatar-size={size}
            type="button"
            data-testid="update-portrait-button"
            // Reopening mid-refresh would show the dialog the portrait we just replaced. Using
            // aria-disabled rather than the disabled attribute keeps the button focusable, so the
            // focus the dialog hands back on close still lands here.
            aria-disabled={avatarRefreshing}
            onClick={avatarRefreshing ? undefined : portraitOverlayProps.handleClick}
            aria-label={t('tree-person.update-portrait.aria-label', {
              name: focusPerson.name ?? t('tree-person.unknown.label'),
            })}
            aria-busy={avatarRefreshing}
            data-status={status}
            data-refreshing={avatarRefreshing || undefined}
          >
            <WhiteTheme avatar-size={size} css={fontSizeCss}>
              {avatarRefreshing && <NoticeLoading color="white" size={size} />}
              {!avatarRefreshing && (status ?? <MediaCamera color="white" size={size} />)}
            </WhiteTheme>
          </button>
          {(portraitOverlayProps.isOpen || portraitOverlayProps.transitioning) && (
            <PortraitOverlay
              onAvatarRefreshingChange={setAvatarRefreshing}
              person={focusPerson}
              overlayProps={portraitOverlayProps}
            />
          )}
        </>
      )}
    </div>
  )
}
